import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { startAuthentication } from "@simplewebauthn/browser";
import AuthService from "../../../api/services/AuthService";
import LoaderHelper from "../../../customComponents/Loading/LoaderHelper";
import { alertErrorMessage, alertSuccessMessage } from "../../../customComponents/CustomAlertMessage";
import "../WithdrawPage/withdrawal.css";
import "./security.css";
import "./disableAccount.css";

const METHOD_PRIORITY = ["passkey", "totp", "email", "mobile"];

function maskEmailForDisplay(raw) {
  if (!raw || typeof raw !== "string") return "you***@email.com";
  const at = raw.indexOf("@");
  if (at <= 0) return "***";
  const local = raw.slice(0, at);
  const domain = raw.slice(at + 1);
  const visible = Math.min(3, local.length);
  return `${local.slice(0, visible)}***@${domain}`;
}

function maskPhoneDigits(digits) {
  const d = String(digits || "").replace(/\D/g, "");
  if (d.length < 4) return "your phone";
  return `***${d.slice(-4)}`;
}

/** Same normalization as disable-account (`get-security-methods-list`). */
function normalizeSecurityMethods(result) {
  const raw =
    result?.data?.security_methods ||
    result?.data?.data?.security_methods ||
    result?.security_methods ||
    result?.data?.securityMethods ||
    null;
  if (!raw || typeof raw !== "object") return null;
  return {
    email: !!raw.email,
    mobile: !!(raw.mobile ?? raw.phone ?? raw.sms),
    totp: !!raw.totp,
    passkey: !!raw.passkey,
  };
}

function pickPreferredVerificationMethod(methods) {
  if (!methods) return null;
  const hit = METHOD_PRIORITY.find((k) => methods[k]);
  return hit || null;
}

const CHOOSE_METHOD_ROW = {
  passkey: {
    title: "Passkey",
    description: "Sign in with Face ID, Touch ID, or your security key.",
    icon: "/images/passkey_vector.svg",
  },
  email: {
    title: "Email OTP",
    description: "Get a one-time code sent to your email",
    icon: "/images/email_vector2.svg",
  },
  mobile: {
    title: "Mobile OTP",
    description: "Receive a verification code via SMS",
    icon: "/images/mobileicon.svg",
  },
  totp: {
    title: "Authenticator App",
    description: "Enter the 6-digit code from your authenticator app",
    icon: "/images/authenticator_icon.svg",
  },
};

function AntiPhishingChooseMethodModal({ methods, onClose, onPick, onSecurityUnavailable }) {
  return (
    <div className="withdraw_confirm_overlay pm-choose-method-overlay-stacked" role="presentation">
      <div className="withdraw_choose_modal pm-choose-method-modal" role="dialog" aria-modal="true" aria-labelledby="aph-choose-verify-title">
        <div className="withdraw_choose_topbar pm-choose-method-modal__topbar">
          <span className="pm-choose-method-modal__topbarSpacer" aria-hidden="true" />
          <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={onClose}>
            ×
          </button>
        </div>

        <h2 id="aph-choose-verify-title" className="withdraw_choose_title pm-choose-method-modal__title">
          Choose Verification Method
        </h2>
        <p className="withdraw_choose_subtitle pm-choose-method-modal__subtitle">
          Select how you&apos;d like to confirm your identity
        </p>

        <div className="emailphone_outerbl pm-choose-method-modal__list">
          {methods.map((key) => {
            const row = CHOOSE_METHOD_ROW[key];
            if (!row) return null;
            return (
              <button key={key} type="button" className="withdraw_choose_item pm-choose-method-item" onClick={() => onPick(key)}>
                <img src={row.icon} alt="" aria-hidden="true" />
                <div className="pm-choose-method-item__text">
                  <span className="pm-choose-method-item__title">{row.title}</span>
                  <span className="pm-choose-method-item__desc">{row.description}</span>
                </div>
                <i className="ri-arrow-right-s-line pm-choose-method-item__chevron" aria-hidden="true" />
              </button>
            );
          })}
        </div>

         <div className="mid_use_text">
          <button type="button" className="withdraw_auth_link" onClick={() => onSecurityUnavailable?.()}>
            Security verification unavailable?
          </button>
        </div> 

        <div className="withdraw_passkey_footer withdraw_auth_footer passkey_footer_text">
          <div className="withdraw_passkey_footer__inner">
            <img src="/images/protected_icon.svg" alt="shield icon" />
            <span>Protected by Balance Risk</span>
          </div>
        </div>
      </div>
    </div>
  );
}

function resolvePasskeyUserId(verifyResult, details) {
  return (
    verifyResult?.data?.userId ??
    details?.userId ??
    details?._id ??
    details?.id ??
    ""
  );
}

/**
 * Turns off anti-phishing code using the same UX pattern as Disable Account:
 * prefetch `get-security-methods-list`, priority passkey → TOTP → email → mobile,
 * POST `anti-phishing/remove`, optional OTP via `anti-phishing/send-otp`.
 *
 * Props: `open`, `onClose`, `userDetails`, `onRemoved`
 */
const AntiPhishingRemoveVerification = ({ open, onClose, userDetails = {}, onRemoved }) => {
  const navigate = useNavigate();

  const accountEmail = String(userDetails?.emailId || userDetails?.email || "").trim();
  const accountPhoneDigits = String(userDetails?.mobileNumber || userDetails?.phone || "").replace(/\D/g, "");
  const signId = accountEmail || accountPhoneDigits;

  const maskedEmail = useMemo(() => maskEmailForDisplay(accountEmail), [accountEmail]);
  const maskedPhone = useMemo(() => maskPhoneDigits(accountPhoneDigits), [accountPhoneDigits]);

  const [resolvedMethods, setResolvedMethods] = useState(null);
  const resolvedMethodsRef = useRef(null);
  const [verifyModal, setVerifyModal] = useState(null);
  const [chooseMethodsOpen, setChooseMethodsOpen] = useState(false);
  const [flowBusy, setFlowBusy] = useState(false);
  const [otpSending, setOtpSending] = useState(false);
  const [otpCode, setOtpCode] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [passkeyAlternateEligible, setPasskeyAlternateEligible] = useState(false);

  const goSecurityUnavailable = useCallback(() => {
    navigate("/asset_managemnet/security_verification_unavailable");
  }, [navigate]);

  const resetVerificationInputs = useCallback(() => {
    setOtpCode("");
    setTotpCode("");
  }, []);

  const closeVerifyFlow = useCallback(() => {
    setVerifyModal(null);
    setChooseMethodsOpen(false);
    setPasskeyAlternateEligible(false);
    resetVerificationInputs();
  }, [resetVerificationInputs]);

  const handleRemoveSuccess = useCallback(
    (message) => {
      const msg =
        typeof message === "string" && message.trim()
          ? message.trim()
          : "Anti-phishing code removed.";
      alertSuccessMessage(msg);
      closeVerifyFlow();
      onRemoved?.();
      onClose?.();
    },
    [closeVerifyFlow, onClose, onRemoved]
  );

  const verifyPasskey = useCallback(
    async (opts = {}) => {
      const { silentCancel = false } = opts;
      if (!signId) {
        alertErrorMessage("Account identifier missing for passkey verification.");
        return "invalid";
      }
      setPasskeyAlternateEligible(false);
      setPasskeyBusy(true);
      LoaderHelper.loaderStatus(true);
      try {
        const optionsResult = await AuthService.passkeyGetStepUpOptions();
        if (!optionsResult?.success || !optionsResult?.data) {
          alertErrorMessage(optionsResult?.message || "Failed to get passkey options");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        let credential;
        LoaderHelper.loaderStatus(false);
        try {
          credential = await startAuthentication(optionsResult.data);
        } catch (webauthnError) {
          LoaderHelper.loaderStatus(false);
          if (webauthnError?.name === "NotAllowedError") {
            if (!silentCancel) {
              alertErrorMessage("Authentication was cancelled or timed out. Try again or use another method.");
            }
            setPasskeyAlternateEligible(true);
            return "cancelled";
          }
          alertErrorMessage("Passkey verification failed.");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        LoaderHelper.loaderStatus(true);
        const verifyResult = await AuthService.passkeyVerifyStepUp(credential);
        if (!verifyResult?.success) {
          alertErrorMessage(verifyResult?.message || "Passkey verification failed");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        const pid = resolvePasskeyUserId(verifyResult, userDetails);
        if (!pid) {
          alertErrorMessage("Could not resolve passkey user id.");
          setPasskeyAlternateEligible(true);
          return "failed";
        }
        const removeResult = await AuthService.antiPhishingRemove({
          verifyMethod: "passkey",
          passkeyUserId: String(pid),
        });
        if (removeResult?.success) {
          handleRemoveSuccess(removeResult?.message);
          return "success";
        }
        alertErrorMessage(removeResult?.message || "Failed to remove anti-phishing code");
        setPasskeyAlternateEligible(true);
        return "failed";
      } catch (e) {
        alertErrorMessage(e?.message || "Passkey verification failed");
        setPasskeyAlternateEligible(true);
        return "failed";
      } finally {
        setPasskeyBusy(false);
        LoaderHelper.loaderStatus(false);
      }
    },
    [signId, userDetails, handleRemoveSuccess]
  );

  const alternateMethodKeys = useMemo(() => {
    const m = resolvedMethods ?? resolvedMethodsRef.current;
    if (!m || !verifyModal) return [];
    return METHOD_PRIORITY.filter((k) => m[k] && k !== verifyModal);
  }, [resolvedMethods, verifyModal]);

  const sendAntiPhishingChannelOtp = useCallback(async (target) => {
    const t = target === "mobile" ? "mobile" : "email";
    setOtpSending(true);
    try {
      const result = await AuthService.antiPhishingSendOtp(t);
      if (result?.success) {
        alertSuccessMessage(result?.message || "Verification code sent");
        return true;
      }
      alertErrorMessage(result?.message || "Failed to send verification code");
      return false;
    } catch (e) {
      alertErrorMessage(e?.message || "Failed to send verification code");
      return false;
    } finally {
      setOtpSending(false);
    }
  }, []);

  const openChooseMethods = useCallback(() => {
    if (alternateMethodKeys.length === 0) {
      alertErrorMessage("No other verification method is available.");
      return;
    }
    setChooseMethodsOpen(true);
  }, [alternateMethodKeys.length]);

  const applyVerificationMethod = useCallback(
    async (method) => {
      setChooseMethodsOpen(false);
      resetVerificationInputs();
      if (method === "passkey") {
        setPasskeyAlternateEligible(false);
        setVerifyModal(null);
        const outcome = await verifyPasskey({ silentCancel: true });
        if (outcome === "invalid") {
          closeVerifyFlow();
          return;
        }
        if (outcome !== "success") {
          setVerifyModal("passkey");
        }
        return;
      }
      setVerifyModal(method);
    },
    [resetVerificationInputs, verifyPasskey, closeVerifyFlow]
  );

  const startPreferredVerification = useCallback(
    async (methodsObj) => {
      const preferred = pickPreferredVerificationMethod(methodsObj);
      if (!preferred) {
        alertErrorMessage("Please add a verification method first");
        return;
      }
      setFlowBusy(true);
      LoaderHelper.loaderStatus(true);
      try {
        resetVerificationInputs();

        if (preferred === "passkey") {
          setChooseMethodsOpen(false);
          setPasskeyAlternateEligible(false);
          setVerifyModal(null);
          const outcome = await verifyPasskey({ silentCancel: true });
          if (outcome === "invalid") {
            closeVerifyFlow();
            return;
          }
          if (outcome !== "success") {
            setVerifyModal("passkey");
          }
          return;
        }

        setVerifyModal(preferred);
      } catch (e) {
        alertErrorMessage(e?.message || "Something went wrong.");
      } finally {
        setFlowBusy(false);
        LoaderHelper.loaderStatus(false);
      }
    },
    [resetVerificationInputs, verifyPasskey, closeVerifyFlow]
  );

  /** When overlay opens: load methods; if unusable → alert & close parent. */
  useEffect(() => {
    if (!open) {
      closeVerifyFlow();
      resolvedMethodsRef.current = null;
      setResolvedMethods(null);
      return undefined;
    }

    let cancelled = false;
    (async () => {
      LoaderHelper.loaderStatus(true);
      try {
        const result = await AuthService.getSecurityMethodsList();
        if (cancelled) return;

        const m = normalizeSecurityMethods(result);
        if (!result?.success || !m) {
          alertErrorMessage(result?.message || "Could not load security methods.");
          onClose?.();
          return;
        }

        const preferred = pickPreferredVerificationMethod(m);
        if (!preferred) {
          alertErrorMessage("Please add a verification method first.");
          onClose?.();
          return;
        }

        resolvedMethodsRef.current = m;
        setResolvedMethods(m);
        await startPreferredVerification(m);
      } catch (e) {
        if (!cancelled) {
          alertErrorMessage(e?.message || "Network error. Please try again.");
          onClose?.();
        }
      } finally {
        LoaderHelper.loaderStatus(false);
      }
    })();

    return () => {
      cancelled = true;
      LoaderHelper.loaderStatus(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally only when dialog opens/closes
  }, [open]);

  const verifyTotpSubmit = async () => {
    const code = String(totpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) {
      alertErrorMessage("Enter the 6-digit authenticator code.");
      return;
    }
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.antiPhishingRemove({
        verifyMethod: "totp",
        code,
      });
      if (res?.success) {
        handleRemoveSuccess(res?.message);
        return;
      }
      alertErrorMessage(res?.message || "Verification failed");
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const verifyEmailOrMobileSubmit = async () => {
    const code = String(otpCode || "").replace(/\D/g, "").slice(0, 6);
    if (code.length !== 6) return;
    const channel = verifyModal === "mobile" ? "mobile" : "email";
    setFlowBusy(true);
    LoaderHelper.loaderStatus(true);
    try {
      const res = await AuthService.antiPhishingRemove({
        verifyMethod: channel,
        code,
      });
      if (res?.success) {
        handleRemoveSuccess(res?.message);
        return;
      }
      alertErrorMessage(res?.message || "Invalid OTP");
    } catch (e) {
      alertErrorMessage(e?.message || "Verification failed");
    } finally {
      setFlowBusy(false);
      LoaderHelper.loaderStatus(false);
    }
  };

  const handlePasteTotp = async () => {
    try {
      const raw = await navigator.clipboard.readText();
      const digits = String(raw || "").replace(/\D/g, "").slice(0, 6);
      if (digits.length === 6) setTotpCode(digits);
    } catch {
      // ignore
    }
  };

  const handleDismiss = () => {
    if (flowBusy || passkeyBusy) return;
    closeVerifyFlow();
    onClose?.();
  };

  const showChooser = chooseMethodsOpen && verifyModal;

  if (!open) return null;

  return (
    <>
      {verifyModal === "passkey" && !showChooser ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation">
          <div className="withdraw_passkey_modal" role="dialog" aria-modal="true" aria-label="Verify with passkey">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={handleDismiss}>
              ×
            </button>
            <div className="withdraw_passkey_title">Verify with passkey</div>
            <div className="withdraw_passkey_hero" aria-hidden="true">
              <img className="light_img" src="/images/passkey_vector_light.svg" alt="" />
              <img className="dark_img" src="/images/passkey_vector.svg" alt="" />
            </div>
            <div className="withdraw_passkey_text">Complete verification using your passkey</div>
            <div className="withdraw_passkey_subtext">
              Please follow the instructions on your browser to complete verification.
            </div>
            <button
              type="button"
              className="withdraw_passkey_verify"
              disabled={passkeyBusy}
              onClick={() => void verifyPasskey({ silentCancel: false })}
            >
              {passkeyBusy ? "Verifying…" : "Verify"}
            </button>
            {alternateMethodKeys.length > 0 && passkeyAlternateEligible ? (
              <button type="button" className="withdraw_passkey_link" onClick={openChooseMethods}>
                Use another verification method
              </button>
            ) : null}
            <button type="button" className="withdraw_passkey_link" onClick={goSecurityUnavailable}>
              Security verification unavailable?
            </button>
            <div className="withdraw_passkey_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {verifyModal === "totp" && !showChooser ? (
        <div className="withdraw_confirm_overlay withdraw_sec_overlay" role="presentation" onClick={(e) => e.stopPropagation()}>
          <div className="withdraw_auth_modal" role="dialog" aria-modal="true" aria-label="Authenticator App Verification">
            <button type="button" className="withdraw_confirm_close" aria-label="Close" onClick={handleDismiss}>
              ×
            </button>
            <div className="withdraw_auth_title">Authenticator App Verification</div>
            <div className="withdraw_auth_subtitle">
              Enter the 6-digit code generated by the
              <br />
              Authenticator App.
            </div>
            <div className="withdraw_auth_label">Authenticator App</div>
            <div className="withdraw_auth_input_row">
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder=""
                value={totpCode}
                onChange={(e) => setTotpCode(String(e.target.value || "").replace(/\D/g, "").slice(0, 6))}
              />
              <button type="button" className="withdraw_auth_paste" onClick={() => void handlePasteTotp()}>
                Paste
              </button>
            </div>
            <button type="button" className="withdraw_auth_submit" disabled={flowBusy} onClick={() => void verifyTotpSubmit()}>
              {flowBusy ? "Verifying…" : "Submit"}
            </button>
            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="withdraw_auth_link" onClick={openChooseMethods}>
                  Switch to Another Verification Method
                </button>
              ) : null}
              <button type="button" className="withdraw_auth_link" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
            </div>
            <div className="withdraw_passkey_footer withdraw_auth_footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {(verifyModal === "email" || verifyModal === "mobile") && !showChooser ? (
        <div className="tf-sec-page__daev-overlay" role="presentation">
          <div
            className="tf-sec-page__daev-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="aph-daev-title"
            onClick={(e) => e.stopPropagation()}
          >
            <button type="button" className="tf-sec-page__daev-close" aria-label="Close" onClick={handleDismiss}>
              ×
            </button>

            <h2 id="aph-daev-title" className="tf-sec-page__daev-title">
              {verifyModal === "mobile" ? "Mobile Verification" : "Email Verification"}
            </h2>
            <p className="tf-sec-page__daev-desc">
              {verifyModal === "mobile"
                ? `Tap Get Code to receive a 6-digit OTP at ${maskedPhone}, then enter it below.`
                : `Tap Get Code to receive a 6-digit OTP at ${maskedEmail}, then enter it below.`}
            </p>

            <label className="tf-sec-page__daev-label" htmlFor="aph-daev-code">
              Enter Verification Code
            </label>
            <div className="tf-sec-page__daev-inputWrap">
              <input
                id="aph-daev-code"
                className="tf-sec-page__daev-input"
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder=""
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              />
              <button
                type="button"
                className="tf-sec-page__daev-getCode"
                disabled={otpSending}
                onClick={() => void sendAntiPhishingChannelOtp(verifyModal)}
              >
                {otpSending ? "Sending…" : "Get Code"}
              </button>
            </div>

            <button
              type="button"
              className="tf-sec-page__daev-submit"
              disabled={otpCode.length !== 6 || flowBusy}
              onClick={() => void verifyEmailOrMobileSubmit()}
            >
              {flowBusy ? "Verifying…" : "Submit"}
            </button>

            <div className="gapbtn_space">
              {alternateMethodKeys.length > 0 ? (
                <button type="button" className="tf-sec-page__daev-help" onClick={openChooseMethods}>
                  Switch verification method
                </button>
              ) : null}
              <button type="button" className="tf-sec-page__daev-help" onClick={goSecurityUnavailable}>
                Security verification unavailable?
              </button>
            </div>

            <div className="tf-sec-page__daev-footer">
              <img src="/images/protected_icon.svg" alt="shield icon" />
              <span>Protected by Balance Risk</span>
            </div>
          </div>
        </div>
      ) : null}

      {showChooser ? (
        <AntiPhishingChooseMethodModal
          methods={alternateMethodKeys}
          onClose={() => setChooseMethodsOpen(false)}
          onPick={(key) => void applyVerificationMethod(key)}
          onSecurityUnavailable={goSecurityUnavailable}
        />
      ) : null}
    </>
  );
};

export default AntiPhishingRemoveVerification;
